import React from "react";
import { Box, Title, Group, Stack } from "@mantine/core";
import { LogoProps, LogoSize } from "./Logo.types";
import { ASSETS } from "@utils/assets";

const getSizeConfig = (size: LogoSize): any => {
  switch (size) {
    case "small":
      return {
        height: 30,
        titleSize: "h5",
        titleWeight: 600,
        subtitleSize: "xs",
      };
    case "medium":
      return {
        height: 40,
        titleSize: "h4",
        titleWeight: 600,
        subtitleSize: "sm",
      };
    case "large":
      return {
        height: 50,
        titleSize: "h2",
        titleWeight: 700,
        subtitleSize: "sm",
      };
    default:
      return getSizeConfig("medium");
  }
};

export const Logo: React.FC<LogoProps> = ({
  size = "medium",
  imageSrc = ASSETS.LOGO, // Default to your logo
  imageAlt = "VenueBoards Logo",
  withName,
  textPosition = "right",
  showTextOnly = false,
  style,
  className,
}) => {
  const sizeConfig = getSizeConfig(size);
  const text = "VenueBoards";

  const logoImage = imageSrc && !showTextOnly && (
    <Box
      style={{
        height: sizeConfig.height,
        display: "flex",
        alignItems: "center",
      }}
    >
      <img
        src={imageSrc}
        alt={imageAlt}
        style={{
          height: "100%",
          width: "auto",
          objectFit: "contain",
        }}
      />
    </Box>
  );

  const logoText = withName && (
    <Box ta={textPosition === "bottom" ? "center" : "left"}>
      <Title
        order={1}
        size={sizeConfig.titleSize as any}
        fw={sizeConfig.titleWeight}
        c="blue.6"
        style={{
          letterSpacing: "-0.5px",
          lineHeight: 1.2,
        }}
      >
        {text}
      </Title>
    </Box>
  );

  // If only text should be shown or no image provided
  if (showTextOnly || !imageSrc) {
    return (
      <Box
        ta="center"
        {...(style && { style })}
        {...(className && { className })}
      >
        {logoText}
      </Box>
    );
  }

  // Show image with text
  if (textPosition === "right") {
    return (
      <Group
        gap="md"
        align="center"
        {...(style && { style })}
        {...(className && { className })}
      >
        {logoImage}
        {logoText}
      </Group>
    );
  }

  // Show image with text below
  return (
    <Stack
      gap="xs"
      align="center"
      {...(style && { style })}
      {...(className && { className })}
    >
      {logoImage}
      {logoText}
    </Stack>
  );
};
